import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faEdit, 
  faTrashAlt,  
  faPlus,
  faImage,
  faVideo,
  faCloudUploadAlt
} from '@fortawesome/free-solid-svg-icons';
import { ADMIN_URLS, API_BASE_URL } from '../../components/config/apiConfig';

function GalleryManager() {
  const [activeTab, setActiveTab] = useState('photos');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    event: '',
    date: '',
    start_date: '',
    end_date: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    event_date: '',
    url: '',
    thumbnail_url: '',
    media_type: 'image',
    file: null
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  const [editData, setEditData] = useState({
    title: '',
    description: '',
    event_date: '',
    url: '',
    featured: false,
    media_type: '',
    file: null
  });
  
  useEffect(() => {
    fetchItems();
  }, [activeTab, currentPage, filter, searchTerm]);
  
  const fetchItems = async () => {
    setIsLoading(true);
    try {
      const mediaType = activeTab === 'photos' ? 'image' : 'video';
      let url = `${ADMIN_URLS.GALLERY}?media_type=${mediaType}&page=${currentPage}`;
      
      if (filter.event) url += `&event=${encodeURIComponent(filter.event)}`;
      if (filter.date) url += `&event_date=${encodeURIComponent(filter.date)}`;
      if (filter.start_date) url += `&start_date=${encodeURIComponent(filter.start_date)}`;
      if (filter.end_date) url += `&end_date=${encodeURIComponent(filter.end_date)}`;
      if (searchTerm) url += `&title=${encodeURIComponent(searchTerm)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch gallery items');
      }
      
      const data = await response.json();
      setItems(data.items || []);
      setTotalPages(data.total_pages || 1);
      setSelectedItems([]);
      setSelectAll(false);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      // Fallback to empty data if API fails
      setItems([]);
      setSelectedItems([]);
      setSelectAll(false);
      setIsLoading(false);
    }
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page on search
    fetchItems();
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter({
      ...filter,
      [name]: value
    });
    setCurrentPage(1); // Reset to first page on filter change
  };
  
  const handleDelete = (item) => {
    setItemToDelete(item);
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      const response = await fetch(`${ADMIN_URLS.GALLERY_ITEM(itemToDelete.id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete item');
      }
      
      // Remove item from state
      setItems(items.filter(item => item.id !== itemToDelete.id));
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item. Please try again.');
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };
  
  const handleItemSelection = (id) => {
    if (selectedItems.includes(id)) {
      setSelectedItems(selectedItems.filter(itemId => itemId !== id));
    } else {
      setSelectedItems([...selectedItems, id]);
    }
  };
  
  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
    setSelectAll(!selectAll);
  };
  
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      const response = await fetch(`${ADMIN_URLS.GALLERY_BULK_DELETE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedItems })
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete items');
      }
      
      // Remove selected items from state
      setItems(items.filter(item => !selectedItems.includes(item.id)));
      setSelectedItems([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error bulk deleting items:', error);
      alert('Failed to delete selected items. Please try again.');
    }
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  // Function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  // Function to generate YouTube thumbnail URL
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
  };
  
  // Function to ensure valid image URL
  const getValidImageUrl = (item) => {
    if (!item) return '';
    
    // Handle YouTube videos
    if (item.media_type === 'video' && item.url) {
      return getYouTubeThumbnail(item.url);
    }
    
    // For images, check thumbnail_url first
    if (item.thumbnail_url) {
      // If it's already a full URL, use it directly
      if (item.thumbnail_url.startsWith('http')) {
          return item.thumbnail_url;
        }
        
        // Fix the path to include 'static' for local files
        let path = item.thumbnail_url;
        
        // Remove the leading slash if present
        if (path.startsWith('/')) {
          path = path.substring(1);
        }
        
        // If path contains 'uploads/gallery', replace with 'static/uploads/gallery'
        if (path.includes('uploads/gallery') && !path.includes('static/')) {
          path = path.replace('uploads/gallery', 'static/uploads/gallery');
        }
        
        // Construct the full URL
        const baseUrl = API_BASE_URL.substring(0, API_BASE_URL.lastIndexOf('/api')); // Remove the '/api' part
        return `${baseUrl}/${path}`;
    }
    
    // If no thumbnail, try using the main URL
    if (item.url) {
      if (item.url.startsWith('http')) {
        return item.url;
      }
      // Fix the path to include 'static' for local files
      let path = item.url;
      
      // Remove the leading slash if present
      if (path.startsWith('/')) {
        path = path.substring(1);
      }
      
      // If path contains 'uploads/gallery', replace with 'static/uploads/gallery'
      if (path.includes('uploads/gallery') && !path.includes('static/')) {
        path = path.replace('uploads/gallery', 'static/uploads/gallery');
      }
      
      // Construct the full URL
      const baseUrl = API_BASE_URL.substring(0, API_BASE_URL.lastIndexOf('/api')); // Remove the '/api' part
      return `${baseUrl}/${path}`;
    }
    
    // Fallback to empty string if no valid URLs found
    return '';
  };
  
  // Handle edit button click
  const handleEdit = (item) => {
    setItemToEdit(item);
    setEditData({
      title: item.title || '',
      description: item.description || '',
      event_date: item.event_date ? item.event_date.substring(0, 10) : '', // Format date for input
      url: item.url || '',
      featured: item.featured || false,
      media_type: item.media_type || (activeTab === 'photos' ? 'image' : 'video'),
      file: null
    });
    setShowEditModal(true);
  };
  
  // Handle edit modal close
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setItemToEdit(null);
  };

  // Handle edit input change
  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditData({
      ...editData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  // Handle file selection for edit
  const handleEditFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditData({
        ...editData,
        file: file
      });
    }
  };
  
  // Handle form submission for editing
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let endpoint = `${ADMIN_URLS.GALLERY_ITEM(itemToEdit.id)}`;
      let method = 'PUT';
      let body;
      
      if (editData.media_type === 'image' && editData.file) {
        // If we have a new file, use FormData
        const formData = new FormData();
        formData.append('file', editData.file);
        formData.append('title', editData.title);
        formData.append('description', editData.description);
        formData.append('event_date', editData.event_date);
        formData.append('featured', editData.featured);
        
        const response = await fetch(endpoint, {
          method: method,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Failed to update item');
        }
      } else {
        // Otherwise use JSON
        body = {
          title: editData.title,
          description: editData.description,
          event_date: editData.event_date,
          featured: editData.featured
        };
        
        // For videos, also update the URL
        if (editData.media_type === 'video') {
          body.url = editData.url;
        }
        
        const response = await fetch(endpoint, {
          method: method,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        
        if (!response.ok) {
          throw new Error('Failed to update item');
        }
      }
      
      // Update the item in the local state
      const updatedItems = items.map(item => {
        if (item.id === itemToEdit.id) {
          return {
            ...item,
            title: editData.title,
            description: editData.description,
            event_date: editData.event_date,
            url: editData.media_type === 'video' ? editData.url : item.url,
            featured: editData.featured
          };
        }
        return item;
      });
      
      setItems(updatedItems);
      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item. Please try again.');
    }
  };
  
  const renderPhotoItems = () => (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAllChange}
              />
            </th>
            <th>Thumbnail</th>
            <th>Title</th>
            <th>Event</th>
            <th>Date</th>
            <th>Featured</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item) => (
              <tr key={item.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => handleItemSelection(item.id)}
                  />
                </td>
                <td>
                  <div 
                    style={{ 
                      width: '80px', 
                      height: '50px', 
                      backgroundImage: `url(${getValidImageUrl(item)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '4px',
                      border: '1px solid #eee',
                    }}
                  />
                </td>
                <td>{item.title}</td>
                <td>{item.description}</td>
                <td>{formatDate(item.event_date)}</td>
                <td>
                  <span style={{
                    backgroundColor: item.featured ? '#d4edda' : '#f8f9fa',
                    color: item.featured ? '#155724' : '#6c757d',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.875rem'
                  }}>
                    {item.featured ? 'Featured' : 'Standard'}
                  </span>
                </td>
                <td>
                  <div className="admin-table-actions">
                    <button 
                      className="admin-table-btn admin-table-btn-edit"
                      onClick={() => handleEdit(item)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="admin-table-btn admin-table-btn-delete"
                      onClick={() => handleDelete(item)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                No photos found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
  
  const renderVideoItems = () => (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAllChange}
              />
            </th>
            <th>Thumbnail</th>
            <th>Title</th>
            <th>Event</th>
            <th>Date</th>
            <th>YouTube URL</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item) => (
              <tr key={item.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedItems.includes(item.id)}
                    onChange={() => handleItemSelection(item.id)}
                  />
                </td>
                <td>
                  <div 
                    style={{ 
                      width: '80px', 
                      height: '50px', 
                      backgroundImage: `url(${getValidImageUrl(item)})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '4px',
                      border: '1px solid #eee',
                      position: 'relative'
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '20px',
                      height: '20px',
                      backgroundColor: 'rgba(0,0,0,0.6)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <div style={{
                        width: 0,
                        height: 0,
                        borderTop: '5px solid transparent',
                        borderBottom: '5px solid transparent',
                        borderLeft: '8px solid white',
                        marginLeft: '2px'
                      }}></div>
                    </div>
                  </div>
                </td>
                <td>{item.title}</td>
                <td>{item.description}</td>
                <td>{formatDate(item.event_date)}</td>
                <td>
                  <a 
                    href={item.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline', color: 'var(--admin-primary)' }}
                  >
                    Watch on YouTube
                  </a>
                </td>
                <td>
                  <div className="admin-table-actions">
                    <button 
                      className="admin-table-btn admin-table-btn-edit"
                      onClick={() => handleEdit(item)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button 
                      className="admin-table-btn admin-table-btn-delete"
                      onClick={() => handleDelete(item)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>
                No videos found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
  
  // Handle upload modal open
  const handleOpenUploadModal = () => {
    setUploadData({
      title: '',
      description: '',
      event_date: '',
      url: '',
      thumbnail_url: '',
      media_type: activeTab === 'photos' ? 'image' : 'video',
      file: null
    });
    setShowUploadModal(true);
  };
  
  // Handle upload modal close
  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
  };

  // Handle upload input change
  const handleUploadInputChange = (e) => {
    const { name, value } = e.target;
    setUploadData({
      ...uploadData,
      [name]: value
    });
  };
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData({
        ...uploadData,
        file: file
      });
    }
  };
  
  // Handle form submission for photos
  const handlePhotoUpload = async (e) => {
    e.preventDefault();
    
    if (activeTab === 'photos' && !uploadData.file) {
      alert('Please select an image file to upload');
      return;
    }
    
    try {
      // For photos, we need to upload the file first
      if (activeTab === 'photos') {
        const formData = new FormData();
        formData.append('file', uploadData.file);
        formData.append('title', uploadData.title);
        formData.append('description', uploadData.description);
        formData.append('event_date', uploadData.event_date);
        formData.append('media_type', 'image');
        
        const response = await fetch(ADMIN_URLS.GALLERY, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error('Failed to upload photo');
        }
        
        // Refresh the gallery
        fetchItems();
        handleCloseUploadModal();
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    }
  };
  
  // Handle form submission for videos
  const handleVideoUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.url) {
      alert('Please enter a YouTube URL');
      return;
    }
    
    try {
      const response = await fetch(ADMIN_URLS.GALLERY, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: uploadData.title,
          description: uploadData.description,
          event_date: uploadData.event_date,
          url: uploadData.url,
          media_type: 'video'
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add video');
      }
      
      // Refresh the gallery
      fetchItems();
      handleCloseUploadModal();
    } catch (error) {
      console.error('Error adding video:', error);
      alert('Failed to add video. Please try again.');
    }
  };
  
  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Gallery Manager</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Gallery Manager</span>
          </li>
        </ul>
      </div>
      
      <div className="admin-card" style={{ marginBottom: '1.5rem' }}>
        <div className="admin-card-body" style={{ padding: '1rem' }}>
          <div className="tabs" style={{ 
            display: 'flex',
            marginBottom: '1.5rem'
          }}>
            <button 
              className={`tab ${activeTab === 'photos' ? 'active' : ''}`}
              onClick={() => setActiveTab('photos')}
              style={{ 
                padding: '0.6rem 1.5rem',
                border: 'none',
                borderBottom: activeTab === 'photos' ? '2px solid var(--admin-primary)' : '2px solid transparent',
                backgroundColor: 'transparent',
                fontWeight: '600',
                color: activeTab === 'photos' ? 'var(--admin-primary)' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faImage} style={{ marginRight: '0.5rem' }} />
              Photos
            </button>
            <button 
              className={`tab ${activeTab === 'videos' ? 'active' : ''}`}
              onClick={() => setActiveTab('videos')}
              style={{ 
                padding: '0.6rem 1.5rem',
                border: 'none',
                borderBottom: activeTab === 'videos' ? '2px solid var(--admin-primary)' : '2px solid transparent',
                backgroundColor: 'transparent',
                fontWeight: '600',
                color: activeTab === 'videos' ? 'var(--admin-primary)' : '#6c757d',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faVideo} style={{ marginRight: '0.5rem' }} />
              Videos
            </button>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ position: 'relative', marginRight: '1rem' }}>
                <input 
                  type="text" 
                  placeholder={`Search ${activeTab}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ 
                    padding: '0.5rem 0.5rem 0.5rem 2rem', 
                    borderRadius: '4px', 
                    border: '1px solid var(--admin-border)',
                    minWidth: '250px'
                  }}
                />
                <FontAwesomeIcon 
                  icon={faSearch} 
                  style={{ 
                    position: 'absolute', 
                    left: '0.5rem', 
                    top: '50%', 
                    transform: 'translateY(-50%)',
                    color: '#6c757d'
                  }}
                />
              </div>
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                style={{ padding: '0.5rem 1rem' }}
              >
                Search
              </button>
            </form>
            
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button 
                onClick={handleOpenUploadModal}
                className="admin-btn admin-btn-primary" 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem', 
                  padding: '0.5rem 1rem' 
                }}
              >
                <FontAwesomeIcon icon={faCloudUploadAlt} />
                {activeTab === 'photos' ? 'Upload Photos' : 'Add YouTube Video'}
              </button>
              
              {selectedItems.length > 0 && (
                <button 
                  className="admin-btn admin-btn-danger"
                  onClick={handleBulkDelete}
                >
                  Delete Selected ({selectedItems.length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : (
        <>
          {activeTab === 'photos' ? renderPhotoItems() : renderVideoItems()}
        </>
      )}
      
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          padding: '1rem', 
          borderTop: '1px solid var(--admin-border)'
        }}>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            <button 
              className="admin-btn"
              style={{ 
                backgroundColor: '#f8f9fa', 
                color: 'var(--admin-dark)', 
                padding: '0.5rem 0.75rem'
              }}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(page => Math.max(page - 1, 1))}
            >
              &laquo;
            </button>
            
            {[...Array(totalPages)].map((_, i) => (
              <button 
                key={i}
                className="admin-btn"
                style={{ 
                  backgroundColor: currentPage === i + 1 ? 'var(--admin-primary)' : '#f8f9fa',
                  color: currentPage === i + 1 ? 'white' : 'var(--admin-dark)',
                  padding: '0.5rem 0.75rem'
                }}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            
            <button 
              className="admin-btn"
              style={{ 
                backgroundColor: '#f8f9fa',
                color: 'var(--admin-dark)',
                padding: '0.5rem 0.75rem'
              }}
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(page => Math.min(page + 1, totalPages))}
            >
              &raquo;
            </button>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '400px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>Confirm Deletion</h2>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete "{itemToDelete?.title}"? This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
                style={{ backgroundColor: '#dc3545', color: 'white' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload Modal */}
      {showUploadModal && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '600px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>
              {activeTab === 'photos' ? 'Upload Photos' : 'Add YouTube Video'}
            </h2>
            
            <form onSubmit={activeTab === 'photos' ? handlePhotoUpload : handleVideoUpload}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Title*
                </label>
                <input 
                  type="text"
                  name="title"
                  value={uploadData.title}
                  onChange={handleUploadInputChange}
                  required
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Event Description
                </label>
                <input 
                  type="text"
                  name="description"
                  value={uploadData.description}
                  onChange={handleUploadInputChange}
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Event Date
                </label>
                <input 
                  type="date"
                  name="event_date"
                  value={uploadData.event_date}
                  onChange={handleUploadInputChange}
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              {activeTab === 'photos' ? (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Photo File*
                  </label>
                  <input 
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    required
                    style={{ 
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    YouTube URL*
                  </label>
                  <input 
                    type="url"
                    name="url"
                    value={uploadData.url}
                    onChange={handleUploadInputChange}
                    required
                    placeholder="e.g. https://www.youtube.com/watch?v=..."
                    style={{ 
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
                <button 
                  type="button"
                  onClick={handleCloseUploadModal}
                  className="admin-btn admin-btn-light"
                  style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="admin-btn admin-btn-primary"
                >
                  {activeTab === 'photos' ? 'Upload Photo' : 'Add Video'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Edit Modal */}
      {showEditModal && itemToEdit && (
        <div style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '600px',
            boxShadow: 'var(--admin-shadow)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginBottom: '1rem' }}>
              Edit {itemToEdit.media_type === 'image' ? 'Photo' : 'Video'}
            </h2>
            
            <form onSubmit={handleEditSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Title*
                </label>
                <input 
                  type="text"
                  name="title"
                  value={editData.title}
                  onChange={handleEditInputChange}
                  required
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Event Description
                </label>
                <input 
                  type="text"
                  name="description"
                  value={editData.description}
                  onChange={handleEditInputChange}
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                  Event Date
                </label>
                <input 
                  type="date"
                  name="event_date"
                  value={editData.event_date}
                  onChange={handleEditInputChange}
                  style={{ 
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: '500' }}>
                  <input 
                    type="checkbox"
                    name="featured"
                    checked={editData.featured}
                    onChange={handleEditInputChange}
                    style={{ marginRight: '0.5rem' }}
                  />
                  Featured Item
                </label>
              </div>
              
              {itemToEdit.media_type === 'image' ? (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Current Photo
                  </label>
                  <div 
                    style={{ 
                      width: '100%',
                      height: '200px',
                      backgroundImage: `url(${getValidImageUrl(itemToEdit)})`,
                      backgroundSize: 'contain',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      borderRadius: '4px',
                      marginBottom: '1rem',
                      border: '1px solid #ddd'
                    }}
                  />
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Replace Photo (Optional)
                  </label>
                  <input 
                    type="file"
                    accept="image/*"
                    onChange={handleEditFileSelect}
                    style={{ 
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
                    YouTube URL*
                  </label>
                  <input 
                    type="url"
                    name="url"
                    value={editData.url}
                    onChange={handleEditInputChange}
                    required
                    placeholder="e.g. https://www.youtube.com/watch?v=..."
                    style={{ 
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                  />
                  {editData.url && (
                    <div 
                      style={{ 
                        width: '100%',
                        height: '200px',
                        backgroundImage: `url(${getYouTubeThumbnail(editData.url)})`,
                        backgroundSize: 'contain',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        borderRadius: '4px',
                        marginTop: '1rem',
                        border: '1px solid #ddd',
                        position: 'relative'
                      }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '60px',
                        height: '60px',
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{
                          width: 0,
                          height: 0,
                          borderTop: '15px solid transparent',
                          borderBottom: '15px solid transparent',
                          borderLeft: '25px solid white',
                          marginLeft: '5px'
                        }}></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
                <button 
                  type="button"
                  onClick={handleCloseEditModal}
                  className="admin-btn admin-btn-light"
                  style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="admin-btn admin-btn-primary"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default GalleryManager;